import { Controller, FormContainer, useFieldArray, useFormContext } from 'react-hook-form-mui';
import { Button, /* RadioGroup, */ Select, Input, Spinner } from 'view/components';
import IndustryDropdownAutocomplete from 'view/pages/profile/components/IndustryDropdownAutocomplete';
import JobCategoryDropdownAutocomplete from 'view/pages/profile/components/JobCategoryDropdownAutocomplete';
import { DropdownsEnum } from 'infrastructure/enums/DropdownsEnum';
import { useGetDropdownData } from 'view/pages/profile/hooks/useGetDropdownData';
import SalaryRange from 'view/components/SalaryRange';
import JobLocationAutocomplete from 'view/pages/profile/components/JobLocationAutocomplete';
import * as S from 'view/pages/profile/WorkPreferences/styled';
import * as PSCS from 'view/pages/profile/ProfileSettings/styled';
import { DeleteIcon } from 'assets';
import { useContextSelector as useCS } from 'use-context-selector';
import { ProfileConfigContext } from 'domain/profileConfig/ProfileConfigContext';
import { profileConfigSelectors } from 'domain/profileConfig/selectors';
import { useGetJobSeekerPreferences } from 'view/pages/profile/hooks/useGetJobSeekerPreferences';
import { useUpdateJobSeekerPreferences } from 'view/pages/profile/hooks/useUpdateJobSeekerPreferences';
import TestSelect from 'view/pages/profile/TestPage/Select';
import TestMultiplySelect from 'view/pages/profile/TestPage/MultiplySelect';
import SearchableMultiplySelect from 'view/pages/profile/TestPage/SearchableMultiplySelect';
import { useEffect } from 'react';
import { Tooltip } from '@mui/material';

const months = [
  { value: 'January', label: 'January' },
  { value: 'February', label: 'February' },
  { value: 'March', label: 'March' },
  { value: 'April', label: 'April' },
  { value: 'May', label: 'May' },
  { value: 'June', label: 'June' },
  { value: 'July', label: 'July' },
  { value: 'August', label: 'August' },
  { value: 'September', label: 'September' },
  { value: 'October', label: 'October' },
  { value: 'November', label: 'November' },
  { value: 'December', label: 'December' },
];

const currentYear = new Date().getFullYear();
const currentMonthIndex = new Date().getMonth();
const expectedEndYear = currentYear + 10;

const years = Array.from({ length: 50 }, (_, i) => {
  return {
    value: String(currentYear - i),
    label: String(currentYear - i),
  };
});

const expectedYears = Array.from({ length: 60 }, (_, i) => {
  return {
    value: String(expectedEndYear - i),
    label: String(expectedEndYear - i),
  };
});

interface Certificate {
  id: string;
  organization: string;
  title: string;
  certificateUrl: string;
}

interface Position {
  id: string;
  companyName: string;
  jobTitle: string;
  positionType: null;

  startDateMonth: string;
  startDateYear: string;
  endDateMonth?: string;
  endDateYear?: string;

  isCurrentCompany: boolean;
  responsibilities: string;
}

interface Education {
  id: string;
  organization: string;
  title: string;
  education: null;
  startDateMonth: string;
  startDateYear: string;
  endDateMonth: string;
  endDateYear: string;
  description: string;
}

interface IFormInput {
  industry: null;
  jobCategory: null;
  jobTitle: null;
  positionType: null;
  salaryRange: { from: string; to: string };
  jobLocation: null;
  onSite: boolean;
  remote: boolean;
  hybrid: boolean;
  languages: null;
  qualifications: null;
  skills: null;
  workExperience: Position[];
  certificates: Certificate[];
  education: Education[];
}

const JobSeekerPreferencesFields = ({
  positionTypeData,
  isPositionTypeDataLoading,
  languageData,
  isLanguageDataLoading,
  isUpdatingJSP,
}) => {
  const {
    watch,
    setValue,
    register,
    clearErrors,
    control,
    formState: { errors },
  } = useFormContext<IFormInput>();

  const industryValue = watch('industry') ?? null;
  const jobCategoryValue: any = watch('jobCategory') ?? null;
  const skillsValue: any = watch('skills') ?? null;
  const jobTitleValue: any = watch('jobTitle') ?? '';
  const salaryRange = watch('salaryRange') || { from: '', to: '' };
  const jobLocationValue = watch('jobLocation') ?? null;

  const {
    fields: certificatesFields,
    append: addCertificate,
    remove: removeCertificate,
  } = useFieldArray({
    name: 'certificates',
  });

  const {
    fields: workExperienceFields,
    append: addPosition,
    remove: removePosition,
  } = useFieldArray({
    name: 'workExperience',
  });

  const {
    fields: educationFields,
    append: addEducation,
    remove: removeEducation,
  } = useFieldArray({
    name: 'education',
  });

  useEffect(() => {
    if (educationFields.length === 0) {
      addEducation(
        {
          id: null,
          organization: '',
          title: '',
          education: null,
          startDateMonth: '',
          startDateYear: '',
          endDateMonth: '',
          endDateYear: '',
          description: '',
        },
        { shouldFocus: false }
      );
    }
  }, [addEducation, educationFields.length]);

  const { data: qualificationData, isLoading: isQualificationLoading } = useGetDropdownData(
    DropdownsEnum.QUALIFICATION
  );
  const { data: skillsData, isLoading: isSkillsLoading } = useGetDropdownData(DropdownsEnum.SKILL);
  const { data: educationData, isLoading: isEducationDataLoading } = useGetDropdownData(
    DropdownsEnum.EDUCATION
  );

  const getAvailableMonths = (selectedYear: string) => {
    if (selectedYear === String(currentYear)) {
      return months.slice(0, currentMonthIndex + 1);
    }
    return months;
  };

  const getAvailableEndMonths = (startMonth: string, startYear: string, endYear: any) => {
    const startIndex = months.findIndex((m) => {
      return m.value === startMonth;
    });
    if (startYear === endYear && endYear === String(currentYear)) {
      return months.slice(startIndex, currentMonthIndex + 1);
    }
    if (startYear === endYear) {
      return months.slice(startIndex);
    }
    return months;
  };

  const getAvailableEducationEndMonths = (startMonth: string, startYear: string, endYear: any) => {
    const startIndex = months.findIndex((m) => {
      return m.value === startMonth;
    });
    if (startYear === endYear) {
      return months.slice(startIndex, months.length);
    }
    return months;
  };

  const isDisabled =
    !!Object.keys(errors).length ||
    isUpdatingJSP ||
    !industryValue ||
    !jobCategoryValue ||
    !jobTitleValue ||
    !skillsValue?.length;

  return (
    <>
      <IndustryDropdownAutocomplete industryValue={industryValue} setValue={setValue} />
      <JobCategoryDropdownAutocomplete jobCategoryValue={jobCategoryValue} setValue={setValue} />
      <Input
        name="jobTitle"
        label="Job Title / Role (Desired) *"
        value={jobTitleValue}
        marginbottom={16}
      />
      {/* <Controller
        name="jobTitle"
        control={control}
        render={({ field }) => {
          return (
            <TestSelect
              {...field}
              label="Job Title / Role (Desired)"
              options={jobTitleOptions || []}
              selectedValue={field.value}
              onChange={(selected) => {
                return field.onChange(selected);
              }}
              disabled={!jobCategoryValue}
            />
          );
        }}
      /> */}
      <TestMultiplySelect
        name="positionType"
        label="Position Type"
        options={positionTypeData || []}
        control={control}
        disabled={isPositionTypeDataLoading || !positionTypeData}
      />
      <SalaryRange
        fromValue={salaryRange.from}
        toValue={salaryRange.to}
        onChange={(field, value) => {
          setValue('salaryRange', { ...salaryRange, [field]: value });
        }}
      />
      <JobLocationAutocomplete
        setValue={setValue}
        jobLocationValue={jobLocationValue}
        marginbottom={16}
      />
      <div>
        <S.TextTitle style={{ marginBottom: '11px' }}>Location Type</S.TextTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', marginBottom: '16px' }}>
          <PSCS.CheckboxWithText>
            <PSCS.CustomCheckbox
              name="showPhone"
              checked={watch('onSite')}
              onChange={(e) => {
                return setValue('onSite', e.target.checked);
              }}
              disabled={false}
            />
            <p
              style={{
                color: '#444444',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '21.76px',
              }}
            >
              On-site
            </p>
          </PSCS.CheckboxWithText>
          <PSCS.CheckboxWithText>
            <PSCS.CustomCheckbox
              name="remote"
              checked={watch('remote')}
              onChange={(e) => {
                return setValue('remote', e.target.checked);
              }}
              disabled={false}
            />
            <p
              style={{
                color: '#444444',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '21.76px',
              }}
            >
              Remote
            </p>
          </PSCS.CheckboxWithText>
          <PSCS.CheckboxWithText>
            <PSCS.CustomCheckbox
              name="hybrid"
              checked={watch('hybrid')}
              onChange={(e) => {
                return setValue('hybrid', e.target.checked);
              }}
              disabled={false}
            />
            <p
              style={{
                color: '#444444',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '21.76px',
              }}
            >
              Hybrid
            </p>
          </PSCS.CheckboxWithText>
        </div>
        <TestMultiplySelect
          name="languages"
          label="Languages"
          options={languageData || []}
          control={control}
          disabled={isLanguageDataLoading || !languageData}
        />
      </div>
      <S.Separator />
      <S.TextWrapper>
        <S.TextTitle>Work Experience</S.TextTitle>
        <S.TextSubTitle>
          {
            'Describe your work experience in detail. \n This will make your CV more appealing to employers.'
          }
        </S.TextSubTitle>
      </S.TextWrapper>
      {/* <RadioGroup name="employee" label="Are you employed?" options={employeeOptions} /> */}
      {workExperienceFields.map((workExperience, index) => {
        const isCurrentCompany = watch(`workExperience.${index}.isCurrentCompany`) || false;
        const startDateMonth = watch(`workExperience.${index}.startDateMonth`);
        const startDateYear = watch(`workExperience.${index}.startDateYear`);
        const endDateMonth = watch(`workExperience.${index}.endDateMonth`);
        const endDateYear = watch(`workExperience.${index}.endDateYear`);
        const startYearErrorMsg = errors?.workExperience?.[index]?.startDateYear?.message;
        const startMonthErrorMsg = errors?.workExperience?.[index]?.startDateMonth?.message;
        const endMonthErrorMsg = errors?.workExperience?.[index]?.endDateMonth?.message;
        const endYearErrorMsg = errors?.workExperience?.[index]?.endDateYear?.message;

        return (
          <S.RemovableContainer key={workExperience.id}>
            <S.RemovableContainerHeader>
              <S.RemovableContainerHeaderTitle>Company</S.RemovableContainerHeaderTitle>
              <S.RemovableContainerHeaderDeleteBtn
                onClick={() => {
                  return removePosition(index);
                }}
              >
                <DeleteIcon />
                <span>Delete</span>
              </S.RemovableContainerHeaderDeleteBtn>
            </S.RemovableContainerHeader>
            <Input
              name={`workExperience.${index}.companyName`}
              label="Company Name"
              placeholder="Enter company name"
              rules={{
                required: 'This field is required',
              }}
              marginbottom={16}
            />
            <Input
              name={`workExperience.${index}.jobTitle`}
              label="Job Title / Role"
              placeholder="Enter job title / role"
              rules={{
                required: 'This field is required',
              }}
              marginbottom={16}
            />
            <Controller
              name={`workExperience.${index}.positionType`}
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field }) => {
                return (
                  <TestSelect
                    {...field}
                    label="Position Type"
                    options={positionTypeData}
                    selectedValue={field.value}
                    onChange={(selected) => {
                      return field.onChange(selected);
                    }}
                    error={errors?.workExperience?.[index]?.positionType?.message} // Ошибка валидации
                    disabled={isPositionTypeDataLoading || !positionTypeData}
                  />
                );
              }}
            />

            {/* ddddd */}
            {/* START DATE */}
            <S.DateContainer>
              <S.DateLabel error={!!startYearErrorMsg || !!startMonthErrorMsg}>
                Start Date
              </S.DateLabel>
              <S.DateSelectWrapper>
                <Select
                  name={`workExperience.${index}.startDateMonth`}
                  options={getAvailableMonths(startDateYear)}
                  selectedValue={startDateMonth || ''}
                  onChange={(value) => {
                    setValue(`workExperience.${index}.startDateMonth`, value);
                    setValue(`workExperience.${index}.endDateMonth`, '');
                    setValue(`workExperience.${index}.endDateYear`, '');
                    clearErrors(`workExperience.${index}.startDateMonth`);
                  }}
                  register={register}
                  rules={{ required: 'This field is required' }} // ✅ Передаем правила валидации
                  error={startMonthErrorMsg} // ✅ Передаем ошибку, если есть
                  disabled={!startDateYear}
                />
                <Select
                  name={`workExperience.${index}.startDateYear`}
                  options={years}
                  selectedValue={startDateYear || ''}
                  onChange={(value) => {
                    setValue(`workExperience.${index}.startDateYear`, value);
                    setValue(`workExperience.${index}.startDateMonth`, '');
                    setValue(`workExperience.${index}.endDateMonth`, '');
                    setValue(`workExperience.${index}.endDateYear`, '');
                    clearErrors(`workExperience.${index}.startDateYear`);
                  }}
                  register={register}
                  rules={{ required: 'This field is required' }} // ✅ Передаем правила валидации
                  error={startYearErrorMsg} // ✅ Передаем ошибку, если есть
                />
              </S.DateSelectWrapper>
            </S.DateContainer>

            {/* CURRENTLY WORKING CHECKBOX */}
            <PSCS.CheckboxWithText style={{ marginBottom: '16px' }}>
              <PSCS.CustomCheckbox
                name={`workExperience.${index}.isCurrentCompany`}
                checked={isCurrentCompany}
                onChange={(e) => {
                  setValue(`workExperience.${index}.isCurrentCompany`, e.target.checked);
                  if (e.target.checked) {
                    setValue(`workExperience.${index}.endDateMonth`, '');
                    setValue(`workExperience.${index}.endDateYear`, '');
                    clearErrors(`workExperience.${index}.endDateMonth`);
                    clearErrors(`workExperience.${index}.endDateYear`);
                  }
                }}
              />
              <p>I am currently working in this company</p>
            </PSCS.CheckboxWithText>

            {/* END DATE */}
            {!isCurrentCompany && (
              <S.DateContainer>
                <S.DateLabel error={!!endMonthErrorMsg || !!endYearErrorMsg}>End Date</S.DateLabel>
                <S.DateSelectWrapper>
                  <Select
                    name={`workExperience.${index}.endDateMonth`}
                    options={getAvailableEndMonths(startDateMonth, startDateYear, endDateYear)}
                    selectedValue={endDateMonth || ''}
                    onChange={(value) => {
                      setValue(`workExperience.${index}.endDateMonth`, value);
                      clearErrors(`workExperience.${index}.endDateMonth`);
                    }}
                    disabled={!startDateMonth || !startDateYear || !endDateYear}
                    register={register}
                    rules={{ required: 'This field is required' }} // ✅ Передаем правила валидации
                    error={endMonthErrorMsg} // ✅ Передаем ошибку, если есть
                  />
                  <Select
                    name={`workExperience.${index}.endDateYear`}
                    options={years.filter((y) => {
                      return Number(y.value) >= Number(startDateYear);
                    })}
                    selectedValue={endDateYear || ''}
                    onChange={(value) => {
                      setValue(`workExperience.${index}.endDateYear`, value);
                      setValue(`workExperience.${index}.endDateMonth`, '');
                      clearErrors(`workExperience.${index}.endDateYear`);
                    }}
                    register={register}
                    rules={{ required: 'This field is required' }} // ✅ Передаем правила валидации
                    error={endYearErrorMsg} // ✅ Передаем ошибку, если есть
                    disabled={!startDateYear}
                  />
                </S.DateSelectWrapper>
              </S.DateContainer>
            )}
            {/* ddddd */}

            <Input
              name={`workExperience.${index}.responsibilities`}
              label="Responsibilities"
              placeholder="Enter your responsibilities..."
              multiline
              rows={6}
            />
          </S.RemovableContainer>
        );
      })}
      <Button
        variant="outlined"
        text="Add Position"
        onClick={() => {
          return addPosition({
            id: null, // ✅ Генерируем уникальный id
            companyName: '',
            jobTitle: '',
            positionType: [],
            startDateMonth: '',
            startDateYear: '',
            endDateMonth: '',
            endDateYear: '',
            isCurrentCompany: false,
            responsibilities: '',
          });
        }}
      />
      <S.Separator />
      <S.TextWrapper>
        <S.TextTitle>Education *</S.TextTitle>
        {/* <S.TextSubTitle>
          {
            'Share information about your education. This will help \n recruiters understand your background better.'
          }
        </S.TextSubTitle> */}
      </S.TextWrapper>
      {educationFields.map((educationItem, index) => {
        const startDateMonth = watch(`education.${index}.startDateMonth`);
        const startDateYear = watch(`education.${index}.startDateYear`);
        const endDateMonth = watch(`education.${index}.endDateMonth`);
        const endDateYear = watch(`education.${index}.endDateYear`);
        const startMonthErrorMsg = errors?.education?.[index]?.startDateMonth?.message;
        const startYearErrorMsg = errors?.education?.[index]?.startDateYear?.message;
        const endMonthErrorMsg = errors?.education?.[index]?.endDateMonth?.message;
        const endYearErrorMsg = errors?.education?.[index]?.endDateYear?.message;

        return (
          <S.RemovableContainer key={educationItem.id}>
            <S.RemovableContainerHeader>
              <S.RemovableContainerHeaderTitle>Organization</S.RemovableContainerHeaderTitle>
              {index > 0 ? (
                <S.RemovableContainerHeaderDeleteBtn
                  onClick={() => {
                    return removeEducation(index);
                  }}
                >
                  <DeleteIcon />
                  <span>Delete</span>
                </S.RemovableContainerHeaderDeleteBtn>
              ) : null}
            </S.RemovableContainerHeader>
            <Input
              name={`education.${index}.organization`}
              label="School"
              placeholder="Enter school name"
              rules={{
                required: 'This field is required',
              }}
              marginbottom={16}
            />
            <Input
              name={`education.${index}.title`}
              label="Field of study"
              placeholder="Enter field of study"
              rules={{
                required: 'This field is required',
              }}
              marginbottom={16}
            />
            <Controller
              name={`education.${index}.education`}
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field }) => {
                return (
                  <TestSelect
                    {...field}
                    label="Education Level"
                    options={educationData || []}
                    selectedValue={field.value}
                    onChange={(selected) => {
                      return field.onChange(selected);
                    }}
                    error={errors?.education?.[index]?.education?.message}
                    disabled={!educationData || isEducationDataLoading}
                  />
                );
              }}
            />

            {/* START DATE */}
            <S.DateContainer>
              <S.DateLabel error={!!startYearErrorMsg || !!startMonthErrorMsg}>
                Start Date
              </S.DateLabel>
              <S.DateSelectWrapper>
                <Select
                  name={`education.${index}.startDateMonth`}
                  options={getAvailableMonths(startDateYear)}
                  selectedValue={startDateMonth || ''}
                  onChange={(value) => {
                    setValue(`education.${index}.startDateMonth`, value);
                    setValue(`education.${index}.endDateMonth`, '');
                    setValue(`education.${index}.endDateYear`, '');
                    clearErrors(`education.${index}.startDateMonth`);
                  }}
                  register={register}
                  rules={{ required: 'This field is required' }} // ✅ Передаем правила валидации
                  error={startMonthErrorMsg} // ✅ Передаем ошибку, если есть
                  disabled={!startDateYear}
                />
                <Select
                  name={`education.${index}.startDateYear`}
                  options={years}
                  selectedValue={startDateYear || ''}
                  onChange={(value) => {
                    setValue(`education.${index}.startDateYear`, value);
                    setValue(`education.${index}.startDateMonth`, '');
                    setValue(`education.${index}.endDateMonth`, '');
                    setValue(`education.${index}.endDateYear`, '');
                    clearErrors(`education.${index}.startDateYear`);
                  }}
                  register={register}
                  rules={{ required: 'This field is required' }} // ✅ Передаем правила валидации
                  error={startYearErrorMsg} // ✅ Передаем ошибку, если есть
                />
              </S.DateSelectWrapper>
            </S.DateContainer>

            {/* END DATE */}
            <S.DateContainer>
              <S.DateLabel error={!!endMonthErrorMsg || !!endYearErrorMsg}>
                End Date (or expected)
              </S.DateLabel>
              <S.DateSelectWrapper>
                <Select
                  name={`education.${index}.endDateMonth`}
                  options={getAvailableEducationEndMonths(
                    startDateMonth,
                    startDateYear,
                    endDateYear
                  )}
                  selectedValue={endDateMonth || ''}
                  onChange={(value) => {
                    setValue(`education.${index}.endDateMonth`, value);
                    clearErrors(`education.${index}.endDateMonth`);
                  }}
                  disabled={!startDateMonth || !startDateYear || !endDateYear}
                  register={register}
                  rules={{ required: 'This field is required' }} // ✅ Передаем правила валидации
                  error={endMonthErrorMsg} // ✅ Передаем ошибку, если есть
                />
                <Select
                  name={`education.${index}.endDateYear`}
                  options={expectedYears.filter((y) => {
                    return Number(y.value) >= Number(startDateYear);
                  })}
                  selectedValue={endDateYear || ''}
                  onChange={(value) => {
                    setValue(`education.${index}.endDateYear`, value);
                    setValue(`education.${index}.endDateMonth`, '');
                    clearErrors(`education.${index}.endDateYear`);
                  }}
                  register={register}
                  rules={{ required: 'This field is required' }} // ✅ Передаем правила валидации
                  error={endYearErrorMsg} // ✅ Передаем ошибку, если есть
                  disabled={!startDateYear}
                />
              </S.DateSelectWrapper>
            </S.DateContainer>

            <Input
              name={`education.${index}.description`}
              label="Description"
              placeholder="Enter your Description..."
              multiline
              rows={6}
            />
          </S.RemovableContainer>
        );
      })}
      <Button
        variant="outlined"
        text="Add Education"
        onClick={() => {
          return addEducation({
            id: null, // ✅ Генерируем уникальный id
            organization: '',
            title: '',
            education: null,
            startDateMonth: '',
            startDateYear: '',
            endDateMonth: '',
            endDateYear: '',
            description: '',
          });
        }}
      />

      <S.Separator />
      <S.TextWrapper>
        <S.TextTitle>Certificates</S.TextTitle>
        {/* <S.TextSubTitle>
          Share information about your certifications to prove your skills
        </S.TextSubTitle> */}
      </S.TextWrapper>
      {certificatesFields.map((certificate, index) => {
        return (
          <S.RemovableContainer key={certificate.id}>
            <S.RemovableContainerHeader>
              <S.RemovableContainerHeaderTitle>
                School or Organization
              </S.RemovableContainerHeaderTitle>
              <S.RemovableContainerHeaderDeleteBtn
                onClick={() => {
                  return removeCertificate(index);
                }}
              >
                <DeleteIcon />
                <span>Delete</span>
              </S.RemovableContainerHeaderDeleteBtn>
            </S.RemovableContainerHeader>
            <Input
              name={`certificates.${index}.organization`}
              label="Organization"
              placeholder="Enter organization name"
              rules={{
                required: 'This field is required',
              }}
              marginbottom={16}
            />
            <Input
              name={`certificates.${index}.title`}
              label="Title"
              placeholder="Enter certificate title"
              rules={{
                required: 'This field is required',
              }}
              marginbottom={16}
            />
            <Input
              name={`certificates.${index}.certificateUrl`}
              label="Certificate URL"
              placeholder="https://"
              /* rules={{
                required: 'This field is required',
              }} */
              marginbottom={1}
            />
          </S.RemovableContainer>
        );
      })}
      <Button
        variant="outlined"
        text="Add Certificate"
        onClick={() => {
          return addCertificate({
            id: null, // ✅ Генерируем уникальный id
            organization: '',
            title: '',
            certificateUrl: '',
          });
        }}
      />
      <S.Separator />
      <S.TextWrapper>
        <S.TextTitle>Qualifications & Skills</S.TextTitle>
        <S.TextSubTitle>Add your Qualifications & Skills</S.TextSubTitle>
      </S.TextWrapper>
      <SearchableMultiplySelect
        name="qualifications"
        label="Qualifications"
        options={qualificationData || []}
        control={control}
        disabled={isQualificationLoading || !qualificationData}
      />
      <SearchableMultiplySelect
        name="skills"
        label="Skills *"
        options={skillsData || []}
        control={control}
        disabled={isSkillsLoading || !skillsData}
      />
      <Tooltip
        title={
          isDisabled && !isUpdatingJSP
            ? 'To save your work preferences, please fill in all required fields or fix all errors on the Work Preferences page.'
            : ''
        }
        arrow
        placement="top"
      >
        <div>
          <Button
            variant="contained"
            type="submit"
            text="Save"
            style={{ margin: '32px 0' }}
            endIcon={
              isUpdatingJSP ? (
                <Spinner
                  disabled={!!Object.keys(errors).length || isUpdatingJSP}
                  variant="contained"
                />
              ) : null
            }
            disabled={isDisabled}
          />
        </div>
      </Tooltip>
    </>
  );
};

const JobSeekerPreferences = () => {
  const { isLoading } = useGetJobSeekerPreferences();
  const { isLoading: isUpdatingJSP, updateJobSeekerPreferences } = useUpdateJobSeekerPreferences();
  const { data: positionTypeData, isLoading: isPositionTypeDataLoading } = useGetDropdownData(
    DropdownsEnum.POSITION_TYPE
  );
  const { data: languageData, isLoading: isLanguageDataLoading } = useGetDropdownData(
    DropdownsEnum.LANGUAGE
  );

  const jobSeekerPreferencesData = useCS(
    ProfileConfigContext,
    profileConfigSelectors.jobSeekerPreferencesData
  );

  const {
    industry,
    jobCategory,
    jobTitle,
    positionType,
    salaryMax,
    salaryMin,
    location,
    jobLocation,
    languages,
    workExperience,
    education,
    certificates,
    qualifications,
    skills,
  } = jobSeekerPreferencesData;

  const onSubmit = (formData: IFormInput) => {
    updateJobSeekerPreferences(formData);
  };

  if (isLoading || isPositionTypeDataLoading || !jobSeekerPreferencesData || !positionTypeData) {
    return (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Spinner variant="outlined" />
      </div>
    );
  }

  const defaultValues = {
    industry: industry || null,
    jobCategory: jobCategory || null,
    jobTitle: jobTitle || null,
    positionType: positionType || null,
    salaryRange: { from: salaryMin || '', to: salaryMax || '' },
    jobLocation: location || null,
    onSite: jobLocation?.onSite || false,
    remote: jobLocation?.remote || false,
    hybrid: jobLocation?.hybrid || false,
    languages: languages || [],
    qualifications: qualifications || null,
    skills: skills || null,
    certificates: certificates || [],
    workExperience: workExperience || [],
    education: education || [],
  };

  return (
    <>
      <S.TextWrapper>
        <S.TextTitle>Desired Position</S.TextTitle>
        <S.TextSubTitle style={{ fontWeight: 'bold', fontSize: '15px' }}>
          Please fill in mandatory fields to complete your profile
        </S.TextSubTitle>
        {/* <S.TextSubTitle>
          {
            'Tell us what you expect from the position \n and we will match you with the right opportunities.'
          }
        </S.TextSubTitle> */}
      </S.TextWrapper>
      <FormContainer
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        onSuccess={onSubmit}
      >
        <JobSeekerPreferencesFields
          positionTypeData={positionTypeData}
          isPositionTypeDataLoading={isPositionTypeDataLoading}
          languageData={languageData}
          isLanguageDataLoading={isLanguageDataLoading}
          isUpdatingJSP={isUpdatingJSP}
        />
      </FormContainer>
    </>
  );
};

export default JobSeekerPreferences;
